import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarDays } from "lucide-react";
import { LOGO, ICLOSED_URL } from "../data/content";

const LINKS = [
  { label: "Accueil", href: "/#top" },
  { label: "Réalisations", href: "/#realisations" },
  { label: "Études de cas", href: "/#cas" },
  { label: "Offre", href: "/#offre" },
  { label: "FAQ", href: "/#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-4 z-50 px-4"
    >
      <nav
        className={`mx-auto flex max-w-6xl items-center justify-between rounded-full border border-[#E6EDF6] px-4 py-2.5 backdrop-blur-xl transition-colors duration-500 ${
          scrolled ? "bg-white/85 shadow-[0_10px_40px_-12px_rgba(37,99,235,0.35)]" : "bg-white/70"
        }`}
      >
        <a href="#top" data-testid="logo-home" className="flex items-center gap-2.5 pl-1">
          <img src={LOGO} alt="Elevate Influence" className="h-8 w-8 rounded-lg object-cover" />
          <span className="font-head text-base font-extrabold text-[#0B1220]">
            Elevate<span className="text-[#3B82F6]"> Influence</span>
          </span>
        </a>

        <div className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace(/^\/#/, "")}`}
              className="text-sm font-semibold text-[#5B6B85] transition-colors duration-300 hover:text-[#0B1220]"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            href={ICLOSED_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta"
            className="btn-blue hidden items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-[#0B1220] sm:inline-flex"
          >
            <CalendarDays size={16} />
            Prendre un rendez-vous
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#0B1220] lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mx-auto mt-2 max-w-6xl overflow-hidden rounded-3xl border border-[#E6EDF6] bg-white p-3 shadow-xl lg:hidden"
          >
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block rounded-xl px-4 py-3 text-base font-semibold text-[#5B6B85] hover:bg-[#0B1220]/[0.04]"
              >
                {l.label}
              </a>
            ))}
            <a
              href={ICLOSED_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="btn-blue mt-2 block rounded-full px-5 py-3 text-center text-sm font-semibold text-[#0B1220]"
            >
              Prendre un rendez-vous
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
